import {
  documentosSugeridos,
  type ConfiguracionDeCarpetas,
  type ResultadoDeRevisionDeCarpetas,
  type TipoDeCarpeta,
} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {
  useEstadoDeCarpetas,
  useGuardarConfiguracionDeCarpetas,
  useModeloDeCarpeta,
  useProbarConexionDeCarpetas,
  useRevisarCarpetas,
} from '../api/carpetas';
import {CarpetasDeDore} from '../componentes/carpetas-de-dore';
import {ListaDeChequeo} from '../componentes/lista-de-chequeo';
import {tr} from '../i18n/idioma';
import {fechaHora, numero} from '../utilidades/formato';

const mensajeDe = (e: unknown) => (e instanceof ErrorDeApi ? e.message : tr('No se pudo.', 'It failed.'));
const campo = 'mt-1 w-full rounded-md border border-linea px-3 py-2 text-sm text-tinta';
const boton = 'rounded-md border border-linea px-3 py-1.5 text-sm hover:bg-fondo disabled:opacity-50';

// Carpetas de las exportaciones en Dropbox (decision 0084, solo administradores):
// por tipo de exportacion, donde van, como se llaman y que subcarpetas llevan.
export function PaginaCarpetasDropbox() {
  const estado = useEstadoDeCarpetas();
  const guardar = useGuardarConfiguracionDeCarpetas();
  const probar = useProbarConexionDeCarpetas();
  const modelo = useModeloDeCarpeta();
  const revisar = useRevisarCarpetas();
  const [config, setConfig] = useState<ConfiguracionDeCarpetas>({activa: false, desde: null, tipos: []});
  const [rutaModelo, setRutaModelo] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [revision, setRevision] = useState<ResultadoDeRevisionDeCarpetas | null>(null);

  useEffect(() => {
    if (estado.data) setConfig(estado.data.configuracion);
  }, [estado.data]);

  const cambiarTipo = (i: number, cambio: Partial<TipoDeCarpeta>) =>
    setConfig(c => ({...c, tipos: c.tipos.map((t, j) => (j === i ? {...t, ...cambio} : t))}));

  const tomarModelo = async () => {
    setMensaje(null);
    try {
      const m = await modelo.mutateAsync(rutaModelo);
      setConfig(c => ({
        ...c,
        tipos: [...c.tipos.filter(t => t.tipo !== m.tipoDeCarpeta.tipo), m.tipoDeCarpeta],
      }));
      setMensaje(
        tr(
          `Tomado de ${m.codigo}${m.omitidas.length ? `; no se tomaron: ${m.omitidas.join(', ')}` : ''}. Revísalo y guarda.`,
          `Taken from ${m.codigo}${m.omitidas.length ? `; left out: ${m.omitidas.join(', ')}` : ''}. Check it and save.`,
        ),
      );
    } catch (e) {
      setMensaje(mensajeDe(e));
    }
  };

  const enviar = async () => {
    setMensaje(null);
    try {
      await guardar.mutateAsync(config);
      setMensaje(tr('Guardado.', 'Saved.'));
    } catch (e) {
      setMensaje(mensajeDe(e));
    }
  };

  const e = estado.data;
  return (
    <section className="max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Carpetas en Dropbox', 'Dropbox folders')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'La app crea la carpeta de cada exportación nueva donde están las demás de su tipo, con sus subcarpetas, y le agrega la factura al financiador (NIMBLQ-xx) cuando se financia. Nunca borra nada en Dropbox. Cada cambio queda en la bitácora.',
            'The app creates the folder of each new export where the others of its kind are, with its subfolders, and adds the invoice to the financer (NIMBLQ-xx) when it is financed. It never deletes anything in Dropbox. Every change is recorded in the audit log.',
          )}
        </p>
      </div>

      <div className="rounded-lg border border-linea bg-white p-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className={e?.conectado ? 'text-verde-oscuro' : 'text-alerta'}>
            {e?.conectado
              ? tr('Dropbox conectado', 'Dropbox connected')
              : tr(
                  'Dropbox no está conectado: faltan las llaves en el servidor.',
                  'Dropbox is not connected: the keys are missing on the server.',
                )}
          </span>
          <button type="button" className={boton} disabled={probar.isPending} onClick={() => probar.mutate()}>
            {tr('Probar la conexión', 'Test the connection')}
          </button>
          {probar.data && (
            <span className={probar.data.disponible ? 'text-verde-oscuro' : 'text-alerta'}>
              {probar.data.disponible
                ? tr(`Funciona (${probar.data.cuenta ?? ''})`, `Works (${probar.data.cuenta ?? ''})`)
                : probar.data.detalle}
            </span>
          )}
        </div>
        {e && (
          <p className="mt-2 text-apagado">
            {tr(
              `${numero(e.vinculadas)} carpetas vinculadas · ${numero(e.conError)} con error · última revisión ${e.ultimaRevision ? fechaHora(e.ultimaRevision) : '—'}`,
              `${numero(e.vinculadas)} linked folders · ${numero(e.conError)} with errors · last check ${e.ultimaRevision ? fechaHora(e.ultimaRevision) : '—'}`,
            )}
          </p>
        )}
      </div>

      <form
        className="space-y-4 rounded-lg border border-linea bg-white p-5 text-sm"
        onSubmit={ev => {
          ev.preventDefault();
          void enviar();
        }}
      >
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={config.activa}
            onChange={ev => setConfig(c => ({...c, activa: ev.target.checked}))}
            className="mt-1 accent-verde"
          />
          <span>
            <span className="font-medium">
              {tr('Crear y ajustar las carpetas solas', 'Create and adjust the folders automatically')}
            </span>
            <span className="mt-0.5 block text-xs text-apagado">
              {tr(
                'Cada pocos minutos. Las exportaciones creadas desde la fecha de abajo reciben carpeta; las anteriores solo se vinculan a la que ya tienen, o con el botón de su ficha.',
                'Every few minutes. Exports created from the date below get a folder; older ones are only linked to the one they already have, or with the button on their page.',
              )}
            </span>
          </span>
        </label>
        <label className="block">
          <span className="font-medium">{tr('Desde', 'From')}</span>
          <input
            type="date"
            value={config.desde ?? ''}
            onChange={ev => setConfig(c => ({...c, desde: ev.target.value || null}))}
            className="mt-1 w-48 rounded-md border border-linea px-3 py-2 text-sm text-tinta"
          />
        </label>

        <div className="rounded-md border border-linea p-4">
          <p className="font-medium">
            {tr('Tomar un tipo de una carpeta de muestra', 'Take a kind from a sample folder')}
          </p>
          <p className="mt-0.5 text-xs text-apagado">
            {tr(
              'Pega la ruta de una carpeta que esté bien armada (la del computador o la de Dropbox), por ejemplo la de la MM89. La app toma la carpeta madre, el nombre y las subcarpetas.',
              'Paste the path of a well-organized folder (the computer one or the Dropbox one), for example MM89. The app takes the parent folder, the name and the subfolders.',
            )}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              value={rutaModelo}
              onChange={ev => setRutaModelo(ev.target.value)}
              className={`${campo} mt-0 flex-1`}
            />
            <button
              type="button"
              className={boton}
              disabled={!rutaModelo.trim() || modelo.isPending || !e?.conectado}
              onClick={() => void tomarModelo()}
            >
              {modelo.isPending ? tr('Leyendo…', 'Reading…') : tr('Tomar', 'Take')}
            </button>
          </div>
        </div>

        {config.tipos.map((t, i) => (
          <fieldset key={t.tipo} className="space-y-2 rounded-md border border-linea p-4">
            <div className="flex items-center justify-between">
              <legend className="font-medium">{tr(`Tipo ${t.tipo}`, `Kind ${t.tipo}`)}</legend>
              <button
                type="button"
                className="text-alerta hover:underline"
                onClick={() => setConfig(c => ({...c, tipos: c.tipos.filter((_, j) => j !== i)}))}
              >
                {tr('Quitar', 'Remove')}
              </button>
            </div>
            <label className="block">
              {tr('Carpeta madre en Dropbox', 'Parent folder in Dropbox')}
              <input
                value={t.carpetaMadre}
                onChange={ev => cambiarTipo(i, {carpetaMadre: ev.target.value})}
                className={campo}
              />
            </label>
            <label className="block">
              {tr('Nombre de la carpeta', 'Folder name')}
              <input value={t.nombre} onChange={ev => cambiarTipo(i, {nombre: ev.target.value})} className={campo} />
            </label>
            <label className="block">
              {tr(
                'Subcarpetas (una por renglón; / para las de adentro)',
                'Subfolders (one per line; / for inner ones)',
              )}
              <textarea
                rows={Math.min(14, Math.max(4, t.subcarpetas.length + 1))}
                value={t.subcarpetas.join('\n')}
                onChange={ev =>
                  cambiarTipo(i, {
                    subcarpetas: ev.target.value
                      .split('\n')
                      .map(s => s.trim())
                      .filter(Boolean),
                  })
                }
                className={`${campo} font-mono`}
              />
            </label>
            <label className="block">
              {tr('Mientras no hay factura, en las subcarpetas va', 'While there is no invoice, subfolders use')}
              <input
                value={t.facturaSinNumero}
                onChange={ev => cambiarTipo(i, {facturaSinNumero: ev.target.value})}
                className="mt-1 w-48 rounded-md border border-linea px-3 py-2 text-sm text-tinta"
              />
            </label>
            <ListaDeChequeo
              documentos={t.documentos}
              sugeridos={() => documentosSugeridos(t.tipo)}
              cambiar={documentos => cambiarTipo(i, {documentos})}
            />
          </fieldset>
        ))}
        <p className="text-xs text-apagado">
          {tr(
            'Marcas: {codigo} = el código de la exportación (C-160, MM052026.89); {numero} = su consecutivo (160); {factura} = la factura al financiador (NIMBLQ-14).',
            'Marks: {codigo} = the export code (C-160, MM052026.89); {numero} = its sequence number (160); {factura} = the invoice to the financer (NIMBLQ-14).',
          )}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={guardar.isPending || estado.isPending}
            className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {guardar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar', 'Save')}
          </button>
          <button
            type="button"
            className={boton}
            disabled={revisar.isPending || !e?.conectado || !e.configuracion.activa}
            onClick={async () => {
              setMensaje(null);
              try {
                setRevision(await revisar.mutateAsync());
              } catch (err) {
                setMensaje(mensajeDe(err));
              }
            }}
          >
            {revisar.isPending ? tr('Revisando…', 'Checking…') : tr('↻ Revisar ahora', '↻ Check now')}
          </button>
          {mensaje && <span className="text-sm">{mensaje}</span>}
        </div>
        {revision && (
          <div className="text-sm">
            <p>
              {tr(
                `${numero(revision.revisadas)} revisadas, ${numero(revision.conCambios)} con cambios, ${numero(revision.conError)} con error.`,
                `${numero(revision.revisadas)} checked, ${numero(revision.conCambios)} changed, ${numero(revision.conError)} with errors.`,
              )}
            </p>
            <ul className="mt-1 list-disc pl-5 text-apagado">
              {revision.acciones.map(a => (
                <li key={a.codigo}>
                  {a.codigo}: {a.acciones.join(' · ')}
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>

      <CarpetasDeDore />
    </section>
  );
}
